import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSelector } from '@legendapp/state/react';
import { useTranslation } from 'react-i18next';
import { Check, Inbox, Library } from 'lucide-react';
import { ICON_SIZE } from '../../design-system';
import { getListsArray, getListArticleCounts, selectList } from '../../store';
import Sheet from './Sheet';

/**
 * List switcher — the one place a sheet is right: a modal pick from a long,
 * scrollable set that returns you to the list you were already in.
 *
 * The current list is marked with a check rather than fill alone, so the state
 * survives for anyone who cannot separate the two fills by colour.
 */
const ListSwitcherSheet = ({ isOpen, onClose }) => {
  const { t } = useTranslation(['sidebar', 'articles']);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { listId } = useParams();

  const lists = useSelector(() => getListsArray());
  const counts = useSelector(() => getListArticleCounts());

  const go = (nextListId, path) => {
    selectList(nextListId);
    navigate(path);
    onClose?.();
  };

  const renderRow = ({ key, icon, label, count, isCurrent, onClick }) => (
    <button
      key={key}
      type="button"
      className="mobile-sheet-row"
      aria-current={isCurrent}
      onClick={onClick}
    >
      {icon}
      <span className="mobile-sheet-row__label">{label}</span>
      <span className="mobile-sheet-row__count">{count}</span>
      {isCurrent && (
        <Check className="mobile-sheet-row__check" size={ICON_SIZE.sm} aria-hidden="true" />
      )}
    </button>
  );

  return (
    <Sheet isOpen={isOpen} onClose={onClose} title={t('sidebar:lists')} scrollable>
      {renderRow({
        key: 'inbox',
        icon: <Inbox size={ICON_SIZE.md} aria-hidden="true" />,
        label: t('sidebar:inbox'),
        count: counts.inbox,
        isCurrent: pathname === '/inbox',
        onClick: () => go(null, '/inbox'),
      })}
      {renderRow({
        key: 'all',
        icon: <Library size={ICON_SIZE.md} aria-hidden="true" />,
        label: t('sidebar:all'),
        count: counts.all,
        isCurrent: pathname === '/all',
        onClick: () => go('all', '/all'),
      })}
      {lists.map((list) => renderRow({
        key: list.id,
        icon: null,
        label: list.name,
        count: counts[list.id] || 0,
        isCurrent: listId === list.id,
        onClick: () => go(list.id, `/list/${list.id}`),
      }))}
    </Sheet>
  );
};

export default ListSwitcherSheet;
